import { inject } from '@angular/core';
import { CanActivateFn, Route, Router } from '@angular/router';

import { AccessDeniedService } from './access-denied.service';
import { AccessRequirement, toRequirements } from './access-requirement';
import { SecurityService } from './security.service';

/** How many module and role pairs a requirement lets in: the larger, the less it demands. */
const breadth = (requirements: AccessRequirement[]): number =>
    new Set(requirements.flatMap(o => o.modules.flatMap(m => o.permissionRoles.map(r => `${m}:${r}`)))).size;

/**
 * For a catch-all declared after the canMatch alternatives for `path` (the 'pharmacy' routes in core.routing.ts,
 * one per lazy module). A signed-in user whose role a module refuses skips it, and when no other alternative
 * takes the URL it would fall through to '**' and land on the dashboard with no explanation.
 *
 * The alternatives that turned the user away say what the page may need. A route for exactly this URL settles
 * it. Otherwise the router never loaded the refusing modules, so which one holds the page is unknown: the least
 * demanding requirement is shown, with a note when others ask more. When nothing turned the user away, no module
 * has the page, and the user goes where '**' would have sent them.
 */
export const fallThroughGuard = (path: string): CanActivateFn => (_route, state) => {
    const router = inject(Router);
    const sec = inject(SecurityService);
    if (!sec.isLoggedOn() || !sec.getUser()) { return sec.loginUrlTree(state.url); }

    const urlPath = state.url.split(/[?#]/)[0].replace(/^\//, '');
    const refusedBy = (o: Route): AccessRequirement[] | null => {
        const requirements = toRequirements(o.data?.['moduleAccess']);
        const meetsAny = requirements.some(r => sec.hasModuleAccess(r.modules, r.permissionRoles));
        return requirements.length && !meetsAny ? requirements : null;
    };
    const deny = (requirements: AccessRequirement[], message?: string) => inject(AccessDeniedService).redirect({
        url: state.url
        , reason: 'module'
        , requirements
        , ...(message ? { message } : {})
    });

    const alternatives = router.config.filter(o => o.canMatch?.length && (o.path === path || o.path === urlPath));
    const exact = alternatives.find(o => o.path === urlPath);
    const exactRefusal = exact ? refusedBy(exact) : null;
    if (exactRefusal) { return deny(exactRefusal); }

    const refusals = alternatives.filter(o => o !== exact).map(refusedBy).filter((o): o is AccessRequirement[] => !!o);
    if (!refusals.length) { return router.parseUrl('/'); }

    const weakest = refusals.reduce((a, b) => breadth(b) > breadth(a) ? b : a);
    const othersAskMore = refusals.some(o => breadth(o) < breadth(weakest));
    return deny(weakest, othersAskMore ? `Some pages under /${path} need a higher role than this.` : undefined);
};
